'use client';

// Legacy: pro-frontend/src/app/portfolio/portfolio.component.ts

import { useEffect, useRef, useState } from 'react';

import {
  buildPublicExtraInfo,
  getPublicPortfolio,
  sendPublicHeartbeat,
} from './api/portfolio';
import { PortfolioTemplateRenderer } from './components/templates';
import {
  filterHiddenForPublic,
  mapResumeToPortfolioData,
  unwrapResume,
} from './lib/map-resume-to-portfolio';
import { resolvePrimaryColor, resolveThemeSelection } from './lib/template-appearance';
import { resolveTemplateKind } from './lib/template-id-map';
import type { PortfolioRenderData, PortfolioTemplateKind } from './types';
import { PublicPortfolioSkeleton } from './PublicPortfolioSkeleton';

import './public.css';

type PublicPortfolioClientProps = {
  id: string;
};

export function PublicPortfolioClient({ id }: PublicPortfolioClientProps) {
  const [data, setData] = useState<PortfolioRenderData | null>(null);
  const [template, setTemplate] = useState<PortfolioTemplateKind>('architect');
  const [theme, setTheme] = useState<'light' | 'dark'>('light');
  const [primaryColor, setPrimaryColor] = useState('#39b6a7');
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const visitorIdRef = useRef<number | null>(null);
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const extraInfo = buildPublicExtraInfo('portfolio');
        const response = await getPublicPortfolio(id, extraInfo);
        if (cancelled) return;

        if (!response || response.status === 'error') {
          setError(true);
          window.location.href = '/';
          return;
        }

        let resume = unwrapResume(response.resume);
        if (!resume) {
          setError(true);
          window.location.href = '/';
          return;
        }

        resume = filterHiddenForPublic(resume);
        const selection = resume.templateSelection;
        setTemplate(
          resolveTemplateKind(
            selection?.template_id ?? resume.template_id,
            resume.template?.template_type,
          ),
        );
        setTheme(resolveThemeSelection(selection, resume.template));
        setPrimaryColor(resolvePrimaryColor(selection, resume.template));

        const mapped = mapResumeToPortfolioData(resume);
        setData(mapped);
        document.title = `View ${mapped.name}'s portfolio on KudosWall`;

        if (response.visitor_id) {
          visitorIdRef.current = response.visitor_id;
          heartbeatRef.current = setInterval(() => {
            if (visitorIdRef.current) {
              void sendPublicHeartbeat(visitorIdRef.current).catch(() => undefined);
            }
          }, 5000);
        }
      } catch {
        if (!cancelled) {
          setError(true);
          window.location.href = '/';
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();

    return () => {
      cancelled = true;
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
      }
    };
  }, [id]);

  if (loading) {
    return <PublicPortfolioSkeleton />;
  }

  if (error || !data) {
    return null;
  }

  return (
    <div className="kw-public-portfolio">
      <PortfolioTemplateRenderer
        template={template}
        data={data}
        isPublicView
        theme={theme}
        primaryColor={primaryColor}
      />
    </div>
  );
}
